import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { CheckCircle2, Crown, Trophy } from 'lucide-react';
import {
  fetchPublicPlans,
  formatMoney,
  friendlyError,
  startCheckoutForPlan,
} from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const SCOPE_LABEL = {
  platform: 'Toda la plataforma',
  club: 'Acceso a un club',
  event: 'Evento concreto',
};

const PERIOD_LABEL = {
  daily: '/día',
  monthly: '/mes',
  yearly: '/año',
  one_time: ' pago único',
};

function PlanCard({ plan, onSubscribe, busy }) {
  const isPlatform = plan.access_scope === 'platform';
  const features = plan.access_scope === 'platform'
    ? [
        'Acceso a todos los eventos premium',
        'Directos y replays incluidos',
        'Descargas no incluidas',
        'Cancela cuando quieras',
      ]
    : [
        'Acceso al contenido premium del club',
        'Directos y replays del club',
        'Cancela cuando quieras',
      ];
  return (
    <Card
      className={`relative p-6 sm:p-7 rounded-3xl border bg-white shadow-[0_8px_28px_-18px_rgba(135,75,20,0.35)] flex flex-col gap-5 ${isPlatform ? 'border-[var(--ns-gold)]/60 ring-2 ring-[var(--ns-gold)]/15' : 'border-[var(--ns-border)]'}`}
      data-testid={`plan-card-${plan.slug}`}
    >
      {isPlatform ? (
        <Badge className="absolute -top-3 right-4 bg-[var(--ns-gold)] text-white px-3 py-1 rounded-full text-[10px] tracking-wider uppercase shadow-md">
          Recomendado
        </Badge>
      ) : null}
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-2xl bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] inline-flex items-center justify-center">
          {isPlatform ? <Crown className="h-5 w-5" /> : <Trophy className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg sm:text-xl font-semibold text-[var(--ns-text)]" data-testid={`plan-name-${plan.slug}`}>
            {plan.name}
          </h3>
          <p className="text-xs uppercase tracking-wider text-[var(--ns-text-subtle)] font-semibold mt-0.5">
            {SCOPE_LABEL[plan.access_scope] || plan.access_scope}
          </p>
        </div>
      </div>
      <p className="text-sm text-[var(--ns-text-muted)] leading-relaxed">
        {plan.description}
      </p>
      <div className="flex items-baseline gap-1">
        <span className="text-3xl sm:text-4xl font-bold text-[var(--ns-text)]" data-testid={`plan-price-${plan.slug}`}>
          {formatMoney(plan.price_cents, plan.currency)}
        </span>
        <span className="text-sm text-[var(--ns-text-muted)]">
          {PERIOD_LABEL[plan.billing_period] || ''}
        </span>
      </div>
      <ul className="space-y-2">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-[var(--ns-text)]">
            <CheckCircle2 className="h-4 w-4 text-[var(--ns-gold-700)] mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button
        type="button"
        onClick={() => onSubscribe(plan)}
        disabled={busy}
        className="mt-auto w-full h-11 rounded-xl bg-[var(--ns-gold)] hover:bg-[var(--ns-gold-700)] text-white font-semibold"
        data-testid={`plan-subscribe-${plan.slug}`}
      >
        {busy ? 'Procesando…' : 'Suscribirse'}
      </Button>
    </Card>
  );
}

export default function PlansList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  // Prompt 4A: consentimiento explícito para planes recurrentes.
  const [pendingPlan, setPendingPlan] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await fetchPublicPlans();
        if (alive) setPlans(data.items || []);
      } catch (err) {
        if (alive) toast.error(friendlyError(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const isRecurring = (p) => p && (p.billing_period === 'daily' || p.billing_period === 'monthly' || p.billing_period === 'yearly');
  const periodLabel = (p) => (p?.billing_period === 'daily'
    ? 'diaria'
    : p?.billing_period === 'monthly'
    ? 'mensual' : p?.billing_period === 'yearly' ? 'anual' : 'pago único');

  const proceedCheckout = async (plan) => {
    setBusyId(plan.id);
    try {
      const consentAt = new Date().toISOString();
      const session = await startCheckoutForPlan(plan.id, {
        consentAcceptedAt: isRecurring(plan) ? consentAt : undefined,
      });
      navigate(`/checkout/sesion/${session.id}`);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setBusyId(null);
      setPendingPlan(null);
      setConsentChecked(false);
    }
  };

  const handleSubscribe = async (plan) => {
    if (!user) {
      toast.info('Inicia sesión para suscribirte.');
      navigate('/acceder?next=/planes');
      return;
    }
    if (plan.access_scope === 'club' && !plan.club_id) {
      toast.info('Selecciona el club al que quieres suscribirte (próximamente desde la ficha del club).');
      return;
    }
    // Para planes recurrentes: forzar consentimiento explícito.
    if (isRecurring(plan)) {
      setPendingPlan(plan);
      setConsentChecked(false);
      return;
    }
    // Planes one_time: sin consentimiento recurrente.
    await proceedCheckout(plan);
  };

  return (
    <MainLayout>
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <header className="mb-8 sm:mb-12 max-w-3xl">
          <p className="text-xs uppercase tracking-[0.22em] text-[var(--ns-gold-700)] font-semibold mb-2">
            Planes NetScouters
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-[var(--ns-text)] mb-3" data-testid="plans-title">
            Elige cómo seguir tu deporte
          </h1>
          <p className="text-base sm:text-lg text-[var(--ns-text-muted)] leading-relaxed">
            Suscríbete para ver todos los eventos premium o paga solo por el evento concreto que te interese.
          </p>
        </header>
        {loading ? (
          <div className="grid sm:grid-cols-2 gap-6">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-72 rounded-3xl" />
            ))}
          </div>
        ) : plans.length === 0 ? (
          <Card className="p-8 text-center rounded-3xl" data-testid="plans-empty">
            <p className="text-[var(--ns-text-muted)]">Aún no hay planes disponibles.</p>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 gap-6" data-testid="plans-grid">
            {plans.map((p) => (
              <PlanCard
                key={p.id}
                plan={p}
                onSubscribe={handleSubscribe}
                busy={busyId === p.id}
              />
            ))}
          </div>
        )}
      </section>
      {/* Prompt 4A: Diálogo de consentimiento de facturación recurrente. */}
      <Dialog
        open={!!pendingPlan}
        onOpenChange={(open) => { if (!open) { setPendingPlan(null); setConsentChecked(false); } }}
      >
        <DialogContent className="sm:max-w-md" data-testid="recurring-consent-dialog">
          <DialogHeader>
            <DialogTitle>Confirmar suscripción recurrente</DialogTitle>
            <DialogDescription>
              Revisa los términos antes de continuar al pago.
            </DialogDescription>
          </DialogHeader>
          {pendingPlan ? (
            <div className="space-y-3 text-sm text-[var(--ns-text)]">
              <div className="rounded-2xl border border-[var(--ns-border)] p-4 space-y-1">
                <div className="flex justify-between">
                  <span className="text-[var(--ns-text-muted)]">Plan</span>
                  <span className="font-semibold" data-testid="recurring-consent-plan-name">
                    {pendingPlan.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--ns-text-muted)]">Precio</span>
                  <span className="font-semibold" data-testid="recurring-consent-price">
                    {formatMoney(pendingPlan.price_cents, pendingPlan.currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--ns-text-muted)]">Periodicidad</span>
                  <span className="font-semibold" data-testid="recurring-consent-period">
                    {periodLabel(pendingPlan)}
                  </span>
                </div>
              </div>
              <label className="flex items-start gap-2 text-xs leading-relaxed cursor-pointer">
                <Checkbox
                  checked={consentChecked}
                  onCheckedChange={(v) => setConsentChecked(!!v)}
                  data-testid="recurring-consent-checkbox"
                />
                <span>
                  Acepto que el pago se renovará automáticamente en cada
                  periodo hasta que cancele la suscripción desde mi cuenta.
                </span>
              </label>
            </div>
          ) : null}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setPendingPlan(null); setConsentChecked(false); }}
              data-testid="recurring-consent-cancel"
            >
              Cancelar
            </Button>
            <Button
              disabled={!consentChecked || busyId === pendingPlan?.id}
              onClick={() => pendingPlan && proceedCheckout(pendingPlan)}
              data-testid="recurring-consent-confirm"
              className="bg-[var(--ns-gold)] hover:bg-[var(--ns-gold-700)] text-white"
            >
              Continuar al pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
